import React, { useEffect, useState, useCallback } from 'react';
import { Target, Download, RefreshCw, MapPin, ChevronDown, ChevronRight, Search, Sparkles, Loader2, ClipboardCheck, CheckCircle2, Map as MapIcon } from 'lucide-react';
import { api } from '../../lib/api';
import StationMarkersMap from '../../components/StationMarkersMap';
import { toast } from 'sonner';

const BRAND_COLORS = {
  'SHELL': 'bg-[#fde047] text-[#713f12]', 'UNO': 'bg-[#dbeafe] text-[#1e40af]',
  'TEXACO': 'bg-[#fee2e2] text-[#991b1b]', 'PUMA': 'bg-[#dcfce7] text-[#166534]',
  'BB': 'bg-[#f3e8ff] text-[#6b21a8]', 'DON ARTURO': 'bg-[#ffedd5] text-[#9a3412]',
};

const BrandChip = ({ brand }) => (
  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${BRAND_COLORS[brand] || 'bg-[#f3f0e9] text-[#3a3a3a]'}`}>{brand}</span>
);

const ScoreBar = ({ value, label }) => (
  <div className="flex items-center gap-1.5" title={`${label}: ${value}`}>
    <span className="text-[10px] text-[#8a8579] w-16">{label}</span>
    <div className="w-14 h-1.5 rounded-full bg-[#f3f0e9] overflow-hidden">
      <div className="h-full rounded-full bg-[#0f1d6f] bar-grow" style={{ width: `${Math.min(100, value)}%` }} />
    </div>
    <span className="text-[10px] font-mono text-[#3a3a3a] w-6">{value}</span>
  </div>
);

const MarkerRow = ({ m, idx }) => (
  <div className="flex items-center gap-3 px-4 py-2.5 border-t border-[#f3f0e9]" data-testid={`marker-row-${m.competitor_id}`}>
    <span className="text-[11px] font-mono text-[#8a8579] w-4">{idx + 1}</span>
    <BrandChip brand={m.brand} />
    <div className="flex-1 min-w-0">
      <p className="text-[12.5px] truncate capitalize">
        {m.name}
        {m.is_current_marker && <span className="ml-2 text-[9px] font-medium px-1.5 py-0.5 rounded bg-[#fee2e2] text-[#991b1b] uppercase tracking-wider">marker actual</span>}
      </p>
      <p className="text-[10.5px] text-[#8a8579]">{m.municipio} · {m.distance_km} km{m.bu ? ` · BU ${m.bu}` : ''}</p>
    </div>
    <div className="hidden md:flex flex-col gap-0.5">
      <ScoreBar value={m.score_traffic} label="Tráfico" />
      <ScoreBar value={m.score_demo} label="Demografía" />
      {m.score_poi !== null && <ScoreBar value={m.score_poi} label={`POIs (${m.poi_count})`} />}
    </div>
    <div className="w-14 text-right">
      <span className="text-[15px] font-semibold text-[#0f1d6f]" data-testid={`marker-score-${m.competitor_id}`}>{m.score}</span>
      <p className="text-[9px] text-[#8a8579] uppercase tracking-wider">score</p>
    </div>
  </div>
);

const StationCard = ({ r, expanded, onToggle, onAdopt, adopting, adopted, onViewMap }) => (
  <div className="bg-white rounded-xl border border-[#e8e5de] overflow-hidden" data-testid={`station-card-${r.own_id}`}>
    <div className="w-full flex items-center hover:bg-[#faf9f5] transition">
      <button onClick={onToggle} className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left" data-testid={`station-toggle-${r.own_id}`}>
        {expanded ? <ChevronDown size={15} className="text-[#8a8579]"/> : <ChevronRight size={15} className="text-[#8a8579]"/>}
        <BrandChip brand={r.brand} />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium truncate">{r.name}</p>
          <p className="text-[11px] text-[#8a8579]">{r.municipio}, {r.department}{r.bu ? ` · BU ${r.bu}` : ''}</p>
        </div>
        {r.volume != null && (
          <div className="hidden sm:block text-right">
            <p className="text-[12px] font-mono">{Math.round(r.volume).toLocaleString()}</p>
            <p className="text-[9px] text-[#8a8579] uppercase tracking-wider">gal/mes</p>
          </div>
        )}
        <div className="hidden sm:block text-right">
          <p className="text-[12px] font-medium">{r.current_marker_count ?? 0}/6</p>
          <p className="text-[9px] text-[#8a8579] uppercase tracking-wider">markers hoy</p>
        </div>
        <div className="text-right w-24">
          <p className="text-[12px] font-medium">{r.markers.length} markers</p>
          <p className="text-[10px] text-[#8a8579]">{r.candidates_in_radius} candidatos</p>
        </div>
      </button>
      <button onClick={() => onViewMap(r)} title="Ver en mapa"
        className="mr-3 shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#e8e5de] text-[11px] text-[#0f1d6f] hover:bg-[#e7eeff] transition"
        data-testid={`view-map-btn-${r.own_id}`}>
        <MapIcon size={12}/> Ver en mapa
      </button>
    </div>
    {expanded && (
      r.markers.length > 0 ? (
        <div className="bg-[#fdfcf9]">
          {r.markers.map((m, i) => <MarkerRow key={m.competitor_id} m={m} idx={i} />)}
          <div className="flex items-center justify-end gap-3 px-4 py-2.5 border-t border-[#f3f0e9]">
            {adopted && <span className="text-[11px] text-[#15803d] inline-flex items-center gap-1" data-testid={`adopted-badge-${r.own_id}`}><CheckCircle2 size={12}/> Markers enlazados</span>}
            <button onClick={() => onAdopt(r)} disabled={adopting || adopted}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1c1c1c] text-white text-[11.5px] hover:bg-black transition disabled:opacity-50"
              data-testid={`adopt-btn-${r.own_id}`}>
              {adopting ? <Loader2 size={12} className="animate-spin"/> : <ClipboardCheck size={12}/>}
              Enlazar estos {r.markers.length} markers
            </button>
          </div>
        </div>
      ) : (
        <p className="px-5 py-4 text-[12px] text-[#8a8579] border-t border-[#f3f0e9]">Sin competidores dentro del radio configurado. Prueba aumentando el radio.</p>
      )
    )}
  </div>
);

export default function AdminMarkers() {
  const [status, setStatus] = useState(null);
  const [topN, setTopN] = useState(5);
  const [radius, setRadius] = useState(3);
  const [dept, setDept] = useState('');
  const [brand, setBrand] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [adoptingId, setAdoptingId] = useState(null);
  const [adoptedIds, setAdoptedIds] = useState(new Set());
  const [mapStation, setMapStation] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/markers/status');
      setStatus(r.data);
      setTopN(r.data.settings.top_n);
      setRadius(r.data.settings.radius_km);
      return r.data;
    } catch { toast.error('No se pudo cargar el estado del módulo'); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (status?.poi_status?.status !== 'running') return;
    const t = setInterval(async () => {
      const s = await loadStatus();
      if (s?.poi_status?.status === 'done') toast.success(`POIs cargados: ${s.poi_status.count?.toLocaleString()}`);
      if (s?.poi_status?.status === 'error') toast.error('Falló la carga de POIs desde OpenStreetMap');
    }, 5000);
    return () => clearInterval(t);
  }, [status?.poi_status?.status, loadStatus]);

  const saveSettingsAndRun = async () => {
    setLoading(true);
    try {
      await api.put('/markers/settings', { top_n: Number(topN), radius_km: Number(radius) });
      const params = { top_n: Number(topN), radius_km: Number(radius) };
      if (dept) params.department = dept;
      if (brand) params.brand = brand;
      if (q) params.q = q;
      const r = await api.get('/markers/recommendations', { params });
      setData(r.data);
      setExpanded(new Set(r.data.items.slice(0, 3).map(i => i.own_id)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error generando recomendaciones');
    } finally { setLoading(false); }
  };

  const enrichPois = async () => {
    try {
      await api.post('/markers/poi-enrich');
      toast.info('Consultando puntos de interés en OpenStreetMap… esto toma unos minutos.');
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar el enriquecimiento');
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (dept) params.department = dept;
      if (brand) params.brand = brand;
      const r = await api.get('/markers/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'recomendacion_markers.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Error exportando'); }
    finally { setExporting(false); }
  };

  const adopt = async (r) => {
    setAdoptingId(r.own_id);
    try {
      const res = await api.post('/markers/adopt', {
        own_id: r.own_id,
        competitor_ids: r.markers.map(m => m.competitor_id),
      });
      const d = res.data;
      const parts = [`${d.newly_linked} enlazadas como markers`];
      if (d.already_linked) parts.push(`${d.already_linked} ya estaban enlazadas`);
      toast.success(`Listo: ${parts.join(', ')}.`);
      if (d.suggested_capturers?.length) {
        const names = d.suggested_capturers.map(c => c.name).join(', ');
        toast.info(`Capturador sugerido: ${names}${d.suggested_from ? ` (cubre "${d.suggested_from.station}" a ${d.suggested_from.distance_km} km)` : ''}. Asígnalo desde Estaciones.`, { duration: 9000 });
      }
      if (d.truncated) toast.warning('La estación ya alcanzó el máximo de 6 markers; algunos no se enlazaron.');
      setAdoptedIds(prev => new Set(prev).add(r.own_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error adoptando markers');
    } finally { setAdoptingId(null); }
  };

  const toggle = (id) => setExpanded(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const poiState = status?.poi_status?.status || 'idle';

  return (
    <div className="space-y-5" data-testid="admin-markers-page">
      {/* Data status + uploads */}
      <div className="bg-white rounded-xl border border-[#e8e5de] p-5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <MapPin size={15} className="text-[#0f1d6f]"/>
            <span className="text-[13px]" data-testid="own-count">Propias: <b>{status?.own_count ?? '—'}</b></span>
          </div>
          <div className="flex items-center gap-2">
            <Target size={15} className="text-[#b91c1c]"/>
            <span className="text-[13px]" data-testid="competitor-count">Competencia: <b>{status?.competitor_count ?? '—'}</b></span>
          </div>
          <span className="text-[11px] text-[#8a8579] px-2 py-0.5 rounded-full bg-[#f3f0e9]" data-testid="source-badge">Fuente: Estaciones AD-P06</span>
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-[#15803d]"/>
            <span className="text-[13px]" data-testid="poi-count">
              POIs (OSM): <b>{status?.poi_count?.toLocaleString() ?? '—'}</b>
              {poiState === 'running' && <span className="ml-1.5 text-[11px] text-[#b45309]">cargando…</span>}
              {poiState === 'error' && <span className="ml-1.5 text-[11px] text-[#b91c1c]">error</span>}
            </span>
          </div>
          <div className="flex-1" />
          <div className="flex flex-wrap gap-2">
            <button onClick={enrichPois} disabled={poiState === 'running'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e8e5de] text-[12px] hover:bg-[#f8f6f1] transition disabled:opacity-50"
              data-testid="poi-enrich-btn">
              {poiState === 'running' ? <Loader2 size={13} className="animate-spin"/> : <Sparkles size={13}/>}
              {status?.poi_count > 0 ? 'Actualizar POIs' : 'Enriquecer con POIs'}
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl border border-[#e8e5de] p-5 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Markers por estación</label>
          <input type="number" min={1} max={20} value={topN} onChange={e => setTopN(e.target.value)}
            className="mt-1 w-24 px-3 py-2 rounded-lg border border-[#e8e5de] text-[13px]" data-testid="topn-input"/>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Radio (km)</label>
          <input type="number" min={0.5} max={25} step={0.5} value={radius} onChange={e => setRadius(e.target.value)}
            className="mt-1 w-24 px-3 py-2 rounded-lg border border-[#e8e5de] text-[13px]" data-testid="radius-input"/>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Departamento</label>
          <select value={dept} onChange={e => setDept(e.target.value)}
            className="mt-1 px-3 py-2 rounded-lg border border-[#e8e5de] text-[13px] bg-white min-w-[160px]" data-testid="dept-filter">
            <option value="">Todos</option>
            {(status?.departments || []).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Bandera propia</label>
          <select value={brand} onChange={e => setBrand(e.target.value)}
            className="mt-1 px-3 py-2 rounded-lg border border-[#e8e5de] text-[13px] bg-white min-w-[120px]" data-testid="brand-filter">
            <option value="">Todas</option>
            {(status?.own_brands || []).map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Buscar estación / BU</label>
          <div className="relative mt-1">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Nombre, municipio o BU…"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-[#e8e5de] text-[13px]" data-testid="search-input"/>
          </div>
        </div>
        <button onClick={saveSettingsAndRun} disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0f1d6f] text-white text-[13px] hover:bg-[#0a1450] transition disabled:opacity-60"
          data-testid="generate-btn">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
          Generar recomendaciones
        </button>
        {data && (
          <button onClick={exportExcel} disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#e8e5de] text-[13px] hover:bg-[#f8f6f1] transition disabled:opacity-60"
            data-testid="export-btn">
            {exporting ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
            Exportar Excel
          </button>
        )}
      </div>

      {/* Results */}
      {!data && !loading && (
        <div className="bg-white rounded-xl border border-dashed border-[#cfcabe] p-12 text-center">
          <Target size={28} className="mx-auto text-[#cfcabe] mb-3"/>
          <p className="text-[14px] font-medium">Recomendación de markers</p>
          <p className="text-[12px] text-[#8a8579] mt-1 max-w-md mx-auto">
            El módulo usa las estaciones de AD-P06: propias (marcas propias) contra competencia.
            Ajusta el número de markers y el radio, y pulsa "Generar recomendaciones" para calcular
            los mejores markers por tráfico compartido, demografía y puntos de interés.
          </p>
        </div>
      )}
      {data && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[12px] text-[#8a8579]" data-testid="results-summary">
              {data.own_count} estaciones propias · top {data.top_n} en radio de {data.radius_km} km
              {data.poi_enabled ? ' · con POIs' : ' · sin POIs (usa "Enriquecer con POIs")'}
            </p>
            <button onClick={() => setExpanded(expanded.size ? new Set() : new Set(data.items.map(i => i.own_id)))}
              className="text-[11.5px] text-[#0f1d6f] hover:underline" data-testid="expand-all-btn">
              {expanded.size ? 'Colapsar todo' : 'Expandir todo'}
            </button>
          </div>
          {data.items.map(r => (
            <StationCard key={r.own_id} r={r} expanded={expanded.has(r.own_id)} onToggle={() => toggle(r.own_id)}
              onAdopt={adopt} adopting={adoptingId === r.own_id} adopted={adoptedIds.has(r.own_id)}
              onViewMap={(rec) => setMapStation({ id: rec.own_id, name: rec.name, brand: rec.brand, lat: rec.lat, lng: rec.lng })} />
          ))}
          {data.items.length === 0 && (
            <p className="text-center text-[12.5px] text-[#8a8579] py-8">Sin resultados con los filtros actuales.</p>
          )}
        </div>
      )}
      {mapStation && (
        <StationMarkersMap station={mapStation} onClose={() => setMapStation(null)} />
      )}
    </div>
  );
}
